import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Navigation, Phone, MessageSquare, ChevronUp, ChevronDown,
  MapPin, Clock, Package, AlertCircle, CheckCircle, Camera
} from 'lucide-react';
import { MobileButton, MobileCard } from '../components/MobileUIKit';
import { MobileBottomNav } from '../components/MobileBottomNav';
import '../styles/mobile.css';

// Mock active route data
const mockRoute = {
  id: 'RT-2026-001',
  totalStops: 3,
  completedStops: 1,
  totalDistance: '8.5 mi',
  estimatedTime: '25 min',
  totalEarnings: 38.50,
  
  currentStop: {
    type: 'dropoff',
    number: 2,
    name: 'John Smith',
    address: '456 Oak Avenue, Apt 3B',
    distance: '1.2 mi',
    time: '5 min',
    phone: '+1 (555) 123-4567',
    instructions: 'Leave at door. Ring doorbell.',
    order: {
      id: 'ORD-2026-4521',
      items: ['2x Classic Burger', '1x Large Fries', '2x Coke'],
      merchant: 'Burger Palace',
    }
  },
  
  stops: [
    { id: 1, type: 'pickup', name: 'Burger Palace', status: 'completed', time: '2:30 PM' },
    { id: 2, type: 'dropoff', name: 'John Smith', status: 'current', address: '456 Oak Ave' },
    { id: 3, type: 'dropoff', name: 'Jane Doe', status: 'pending', address: '789 Pine St' },
  ]
};

export const DriverRoutePage = () => {
  const navigate = useNavigate();
  const [isSheetExpanded, setIsSheetExpanded] = useState(false);

  const handleNavigate = () => {
    // Would open Google Maps/Apple Maps with the address
    window.open(`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(mockRoute.currentStop.address)}`, '_blank');
  };

  const handleCompleteStop = () => {
    navigate('/driver/pod');
  };

  return (
    <div className="mobile-app min-h-screen">
      {/* Map Area */}
      <div 
        className="relative w-full"
        style={{ height: isSheetExpanded ? '30vh' : '55vh' }}
      >
        {/* Placeholder Map - In production, use react-leaflet or Google Maps */}
        <div 
          className="w-full h-full"
          style={{ 
            background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
          }}
        >
          {/* Mock map with route visualization */}
          <svg className="w-full h-full" viewBox="0 0 400 300">
            {/* Grid lines */}
            <defs>
              <pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse">
                <path d="M 40 0 L 0 0 0 40" fill="none" stroke="rgba(255,255,255,0.05)" strokeWidth="1"/>
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#grid)" />
            
            {/* Route line */}
            <path 
              d="M 100 250 Q 150 200 200 180 T 300 100" 
              fill="none" 
              stroke="#E53935" 
              strokeWidth="4"
              strokeLinecap="round"
              strokeDasharray="8 4"
            />
            
            {/* Current location */}
            <circle cx="100" cy="250" r="12" fill="#E53935">
              <animate attributeName="r" values="12;16;12" dur="2s" repeatCount="indefinite"/>
            </circle>
            <circle cx="100" cy="250" r="6" fill="white"/>
            
            {/* Stop 2 (current destination) */}
            <circle cx="200" cy="180" r="10" fill="#4CAF50"/>
            <text x="200" y="184" textAnchor="middle" fill="white" fontSize="12" fontWeight="bold">2</text>
            
            {/* Stop 3 */}
            <circle cx="300" cy="100" r="10" fill="#666"/>
            <text x="300" y="104" textAnchor="middle" fill="white" fontSize="12" fontWeight="bold">3</text>
          </svg>

          {/* Map Controls */}
          <div className="absolute right-4 top-20 flex flex-col gap-2">
            <button 
              className="w-11 h-11 rounded-full flex items-center justify-center"
              style={{ background: 'var(--mobile-bg-elevated)', border: '1px solid var(--mobile-border)' }}
            >
              <Navigation className="w-5 h-5" style={{ color: 'var(--mobile-text-primary)' }} />
            </button>
            <button 
              className="w-11 h-11 rounded-full flex items-center justify-center"
              style={{ background: 'var(--mobile-bg-elevated)', border: '1px solid var(--mobile-border)' }}
            >
              +
            </button>
            <button 
              className="w-11 h-11 rounded-full flex items-center justify-center"
              style={{ background: 'var(--mobile-bg-elevated)', border: '1px solid var(--mobile-border)' }}
            >
              −
            </button>
          </div>
        </div>

        {/* Gradient overlay at bottom */}
        <div 
          className="absolute bottom-0 left-0 right-0 h-20 pointer-events-none"
          style={{ 
            background: 'linear-gradient(to top, var(--mobile-bg-primary), transparent)'
          }}
        />
      </div>

      {/* Bottom Sheet */}
      <div 
        className="fixed bottom-0 left-0 right-0 rounded-t-3xl transition-all duration-300"
        style={{ 
          background: 'var(--mobile-bg-elevated)',
          height: isSheetExpanded ? '70vh' : '45vh',
          paddingBottom: '80px'
        }}
      >
        {/* Drag Handle */}
        <button 
          onClick={() => setIsSheetExpanded(!isSheetExpanded)}
          className="w-full py-3 flex justify-center"
        >
          <div 
            className="w-10 h-1.5 rounded-full"
            style={{ background: 'var(--mobile-text-disabled)' }}
          />
        </button>

        <div className="px-4 overflow-y-auto" style={{ height: 'calc(100% - 40px)' }}>
          {/* Current Stop Header */}
          <div className="mobile-flex mobile-justify-between mobile-items-start mb-4">
            <div>
              <p className="mobile-text-tiny" style={{ color: 'var(--mobile-text-tertiary)' }}>
                STOP {mockRoute.currentStop.number} of {mockRoute.totalStops} • {mockRoute.currentStop.type.toUpperCase()}
              </p>
              <h2 
                className="mobile-text-title mt-1"
                style={{ color: 'var(--mobile-text-primary)' }}
              >
                {mockRoute.currentStop.name}
              </h2>
            </div>
            <div className="text-right">
              <p className="mobile-text-tiny" style={{ color: 'var(--mobile-text-tertiary)' }}>Est. earnings</p>
              <p 
                className="font-bold text-lg"
                style={{ color: 'var(--mobile-earnings)' }}
              >
                ${mockRoute.totalEarnings.toFixed(2)}
              </p>
            </div>
          </div>

          {/* Address & Distance */}
          <MobileCard className="mb-4">
            <div className="mobile-flex mobile-items-start mobile-gap-3">
              <MapPin className="w-5 h-5 mt-0.5" style={{ color: 'var(--mobile-success)' }} />
              <div className="flex-1">
                <p 
                  className="font-medium"
                  style={{ color: 'var(--mobile-text-primary)' }}
                >
                  {mockRoute.currentStop.address}
                </p>
                <div className="mobile-flex mobile-gap-3 mt-2">
                  <span className="mobile-text-tiny mobile-flex mobile-items-center mobile-gap-1">
                    <Navigation className="w-3 h-3" />
                    {mockRoute.currentStop.distance}
                  </span>
                  <span className="mobile-text-tiny mobile-flex mobile-items-center mobile-gap-1">
                    <Clock className="w-3 h-3" />
                    {mockRoute.currentStop.time}
                  </span>
                </div>
              </div>
            </div>
          </MobileCard>

          {/* Contact Actions */}
          <div className="mobile-flex mobile-gap-2 mb-4">
            <button 
              className="flex-1 mobile-btn mobile-btn-secondary py-3"
              onClick={() => window.open(`tel:${mockRoute.currentStop.phone}`)}
            >
              <Phone className="w-4 h-4 mr-2" />
              Call
            </button>
            <button className="flex-1 mobile-btn mobile-btn-secondary py-3">
              <MessageSquare className="w-4 h-4 mr-2" />
              Chat
            </button>
          </div>

          {/* Navigate Button */}
          <MobileButton 
            variant="primary" 
            fullWidth 
            size="large"
            icon={Navigation}
            onClick={handleNavigate}
            className="mb-4"
          >
            Navigate
          </MobileButton>

          {/* Order Details */}
          {isSheetExpanded && (
            <div className="mobile-animate-fade-in">
              <MobileCard className="mb-4">
                <div className="mobile-flex mobile-items-center mobile-gap-2 mb-3">
                  <Package className="w-4 h-4" style={{ color: 'var(--mobile-text-secondary)' }} />
                  <p className="mobile-text-caption font-medium">ORDER DETAILS</p>
                </div>
                <p 
                  className="font-medium mb-2"
                  style={{ color: 'var(--mobile-text-primary)' }}
                >
                  {mockRoute.currentStop.order.merchant}
                </p>
                <ul className="space-y-1">
                  {mockRoute.currentStop.order.items.map((item, i) => (
                    <li 
                      key={i}
                      className="mobile-text-caption"
                      style={{ color: 'var(--mobile-text-secondary)' }}
                    >
                      • {item}
                    </li>
                  ))}
                </ul>
                <p className="mobile-text-tiny mt-3" style={{ color: 'var(--mobile-text-tertiary)' }}>
                  Order #{mockRoute.currentStop.order.id}
                </p>
              </MobileCard>

              {/* Special Instructions */}
              {mockRoute.currentStop.instructions && (
                <MobileCard 
                  className="mb-4"
                  style={{ 
                    background: 'var(--mobile-warning-muted)',
                    border: '1px solid var(--mobile-warning)'
                  }}
                >
                  <div className="mobile-flex mobile-items-start mobile-gap-2">
                    <AlertCircle className="w-4 h-4 mt-0.5" style={{ color: 'var(--mobile-warning)' }} />
                    <div>
                      <p className="text-sm font-medium" style={{ color: 'var(--mobile-warning)' }}>
                        Special Instructions
                      </p>
                      <p className="mobile-text-caption mt-1" style={{ color: 'var(--mobile-text-primary)' }}>
                        {mockRoute.currentStop.instructions}
                      </p>
                    </div>
                  </div>
                </MobileCard>
              )}

              {/* All Stops */}
              <h4 className="mobile-text-caption font-medium mb-2" style={{ color: 'var(--mobile-text-tertiary)' }}>
                ALL STOPS
              </h4>
              {mockRoute.stops.map((stop, index) => (
                <div 
                  key={stop.id}
                  className="mobile-flex mobile-items-center mobile-gap-3 py-3 border-b"
                  style={{ borderColor: 'var(--mobile-divider)' }}
                >
                  <div 
                    className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold"
                    style={{ 
                      background: stop.status === 'completed' 
                        ? 'var(--mobile-success)' 
                        : stop.status === 'current'
                        ? 'var(--ezp-primary)'
                        : 'var(--mobile-bg-tertiary)',
                      color: stop.status === 'pending' ? 'var(--mobile-text-tertiary)' : 'white'
                    }}
                  >
                    {stop.status === 'completed' ? '✓' : index + 1}
                  </div>
                  <div className="flex-1">
                    <p 
                      className="font-medium text-sm"
                      style={{ 
                        color: stop.status === 'pending' 
                          ? 'var(--mobile-text-tertiary)' 
                          : 'var(--mobile-text-primary)' 
                      }}
                    >
                      {stop.name}
                    </p>
                    <p className="mobile-text-tiny">
                      {stop.type === 'pickup' ? 'Pickup' : 'Dropoff'}
                      {stop.time && ` • ${stop.time}`}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Complete Delivery Button */}
          <MobileButton 
            variant="success" 
            fullWidth 
            size="large"
            icon={Camera}
            onClick={handleCompleteStop}
            className="mt-4"
          >
            Complete Delivery
          </MobileButton>
        </div>
      </div>

      {/* Bottom Navigation */}
      <MobileBottomNav />
    </div>
  );
};

export default DriverRoutePage;
